import {
  MotifNavigationIcMoreVert24px,
  MotifImageIcEdit24px,
  MotifActionIcDelete24px,
} from '@ey-xd/motif-icon'
import {
  MotifCard,
  MotifCardHeader,
  MotifCardFooter,
  MotifCardBody,
  MotifIcon,
  MotifIconButton,
  MotifButton,
  MotifDropdown,
  MotifDropdownItem,
  MotifLabel,
  MotifTooltip,
} from '@ey-xd/motif-react'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

import type { IUserSearchOption } from '@/common/models/User'
import { UserAvatar } from '@/components/UserAvatar/UserAvatar'
import { useGetUserEmailQuery } from '@/services/graphApi'
import DeleteModal from '../../common/components/DeleteModal'
import { EngagementCardData, TeamMemberType } from '../../services/types'
import { useLazyDeleteClientQuery } from './clientApi'
import {
  deleteClient,
  openClientCard,
  setCurrentEngagement,
} from './clientCardSlice'
import styles from './index.module.scss'

// Engagement card only, for now - shows the team member's email in a hover
// tooltip (falls back to userPrincipalName if mail isn't set on the account).
const TeamMemberAvatar: React.FC<{ user: IUserSearchOption }> = ({ user }) => {
  const { data: email } = useGetUserEmailQuery(user.userId)

  return (
    <MotifTooltip
      hideCloseButton
      allowHover
      trigger={
        <UserAvatar
          adIdentifier={user.userId}
          label={user.userName}
          profileImage={user.icon}
          className={styles.circle}
        />
      }
    >
      <div className="motif-caption-default-light">{email ?? user.userName}</div>
    </MotifTooltip>
  )
}

export function EngagementCard({
  engagement: {
    id,
    engagementName,
    clientName,
    signOffDueDate,
    engagementCode,
    team,
    applicationsCount,
    isArchived,
  }
}: {
  engagement: EngagementCardData,
}) {
  const navigate = useNavigate()
  const [deleteClientQuery] = useLazyDeleteClientQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const dispatch = useDispatch()
  const currentUserId = useSelector(
    (state: { user: { id: string } }) => state.user.id,
  )
  const [isOwner, setIsOwner] = useState(false)

  useEffect(() => {
    if (currentUserId) {
      const teamMember = team.find(
        (user: IUserSearchOption) => user.userId === currentUserId,
      )
      if (teamMember) {
        setIsOwner(teamMember.memberType === TeamMemberType.Owner)
      }
    }
  }, [currentUserId, team])

  function getEngagementDueDate(iso: Date | null | undefined): JSX.Element {
    if (iso === null || iso === undefined) return <></>
    const currentDate = moment(new Date(), 'YYYY-MM-DD') // Use local time zone
    const dueDate = moment(iso, 'YYYY-MM-DD')
    const timeDifference = dueDate.diff(currentDate, 'days', true)
    if (timeDifference <= 0) {
      return (
        <div className="dueDate dueRed">
          {Math.abs(Math.round(timeDifference))} days Overdue
        </div>
      )
    } else if (timeDifference < 10) {
      return (
        <div className="dueDate dueRed">
          {Math.round(timeDifference) + 1} days remaining to Audit Report Date
        </div>
      )
    } else if (timeDifference >= 10 && timeDifference <= 29) {
      return (
        <div className="dueDate dueOrange">
          {Math.round(timeDifference) + 1} days remaining to Audit Report Date
        </div>
      )
    } else {
      return (
        <div className="dueDate">
          Due in {Math.floor(timeDifference) + 1} days to Audit Report Date
        </div>
      )
    }
  }

  const handleDeleteClient = () => {
    setIsDeleteModalVisible(false)
    if (id) {
      deleteClientQuery(id).then(() => {
        dispatch(deleteClient(id))
      })
    }
  }

  const openEditClientCardModal = () => {
    console.log('openEditClientCardModal', id)
    dispatch(openClientCard(id))
  }

  const handleNavigate = (
    id: string,
    engagementName: string,
    clientName: string,
  ) => {
    dispatch(
      setCurrentEngagement({
        engagementID: id,
        engagementName,
        clientID: id,
        clientName,
        isArchived,
      }),
    )
    navigate(`/${id}`)
  }

  return (
    <>
      <MotifCard className={styles.clientCard}>
        <MotifCardHeader className={styles.clientCardHeader}>
          <div className={styles.cardHeaderTitle}>
            <span className="motif-h5" title={engagementName}>
              {engagementName}
            </span>
          </div>
          {isOwner && (
            <div>
              <MotifDropdown
                closeOnOutsideClick={ true }
                handleOnClose={() => setIsOpen(false)}
                open={isOpen}
                trigger={
                  <MotifIconButton onClick={() => setIsOpen(!isOpen)}>
                    <MotifIcon
                      src={MotifNavigationIcMoreVert24px}
                      title="More options"
                    />
                  </MotifIconButton>
                }
              >
                <MotifDropdownItem onClick={() => openEditClientCardModal()}>
                  <MotifIcon src={MotifImageIcEdit24px} />
                  Edit
                </MotifDropdownItem>
                <MotifDropdownItem
                  onClick={() => setIsDeleteModalVisible(true)}
                >
                  <MotifIcon src={MotifActionIcDelete24px} />
                  Delete
                </MotifDropdownItem>
              </MotifDropdown>
            </div>
          )}
        </MotifCardHeader>
        <MotifCardBody className={styles.clientCardBody} key={id}>
          <div className={styles.tileContainer}>
            <MotifLabel className={styles.tileLabel}>Engagement ID </MotifLabel>
            <span className={styles.tileData}>: {engagementCode}</span>
          </div>
          <div className={styles.tileContainer}>
            <MotifLabel className={styles.tileLabel}>Client</MotifLabel>
            <span className={styles.tileData}>: {clientName}</span>
          </div>
          <div className={styles.tileContainer}>
            <MotifLabel>Applications</MotifLabel>
            <span className={styles.tileData}>
              : {applicationsCount && applicationsCount > 0 ? applicationsCount : '--'}
            </span>
          </div>
          <div className={styles.tileContainer}>
            {getEngagementDueDate(
              signOffDueDate ? new Date(signOffDueDate) : null,
            )}
          </div>
          <div
            className={`motif-container motif-row no-gutter align-items-left`}
          >
            {Object.values(team).map((user: IUserSearchOption) => (
              <TeamMemberAvatar key={user.userId} user={user} />
            ))}
          </div>
        </MotifCardBody>
        <MotifCardFooter className={styles.clientCardFooter}>
          <MotifButton
            variant={isArchived ? 'secondary' : 'primary'}  
            title="Get Started"
            onClick={() =>
              id &&
              engagementName &&
              clientName &&
              handleNavigate(id, engagementName, clientName)
            }
          >
            {isArchived ? 'View Details' :'Get Started'}
          </MotifButton>
        </MotifCardFooter>
      </MotifCard>

      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        handleDelete={handleDeleteClient}
        isMessageVisible={false}
      />
    </>
  )
}

export default EngagementCard
